import React, { useMemo } from "react";
import { computePageWindow } from "lib/pagination";
import { DATA_TABLE_PAGINATION_BAR } from "lib/dataTableClasses";

const navBtn =
  "rounded-lg bg-gray-100 px-2 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50 dark:bg-white/10 dark:text-white";
const navBtnWide =
  "rounded-lg bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50 dark:bg-white/10 dark:text-white";

function pageButtonClass(active: boolean): string {
  return `h-7 min-w-[28px] rounded-md px-2 text-xs font-semibold ${
    active
      ? "bg-brand-500 text-white"
      : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/10 dark:text-white"
  }`;
}

/**
 * Shared pagination + range summary for admin data tables.
 */
export default function DataTablePagination({
  isArabic,
  page,
  totalPages,
  fromItem,
  toItem,
  totalItems,
  onPageChange,
  className = "",
}: {
  isArabic: boolean;
  page: number;
  totalPages: number;
  fromItem: number;
  toItem: number;
  totalItems: number;
  onPageChange: (p: number) => void;
  className?: string;
}) {
  /** API may report 0; treat as one page so page buttons still render. */
  const safeTotalPages = Math.max(1, totalPages);

  const pageWindow = useMemo(
    () => computePageWindow(page, safeTotalPages),
    [page, safeTotalPages]
  );

  if (totalItems <= 0) return null;

  return (
    <div className={`${DATA_TABLE_PAGINATION_BAR} ${className}`.trim()}>
      <div className="grid grid-cols-1 items-center gap-3 sm:grid-cols-3 sm:gap-2">
        <div
          className={`flex justify-center sm:block ${isArabic ? "sm:text-right" : "sm:text-left"}`}
        >
          <div
            className={`inline-flex items-center gap-2 ${isArabic ? "flex-row-reverse" : ""}`}
          >
            <button
              type="button"
              onClick={() => onPageChange(1)}
              disabled={page <= 1}
              className={navBtn}
              title={isArabic ? "أول صفحة" : "First page"}
            >
              «
            </button>
            <button
              type="button"
              onClick={() => onPageChange(Math.max(1, page - 1))}
              disabled={page <= 1}
              className={navBtnWide}
            >
              {isArabic ? "السابق" : "Previous"}
            </button>
          </div>
        </div>
        <div className="order-first flex flex-col items-center justify-center gap-2 sm:order-none">
          <span className="text-center text-xs text-gray-600 dark:text-gray-300">
            {isArabic
              ? `الصفحة ${page} من ${safeTotalPages} | عرض ${fromItem}-${toItem} من ${totalItems}`
              : `Page ${page} of ${safeTotalPages} | Showing ${fromItem}-${toItem} of ${totalItems}`}
          </span>
          <div className="flex max-w-full flex-wrap items-center justify-center gap-1">
            {pageWindow.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => onPageChange(p)}
                className={pageButtonClass(p === page)}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
        <div
          className={`flex justify-center sm:block ${isArabic ? "sm:text-left" : "sm:text-right"}`}
        >
          <div
            className={`inline-flex items-center gap-2 ${isArabic ? "flex-row-reverse" : ""}`}
          >
            <button
              type="button"
              onClick={() => onPageChange(page + 1)}
              disabled={page >= safeTotalPages}
              className={navBtnWide}
            >
              {isArabic ? "التالي" : "Next"}
            </button>
            <button
              type="button"
              onClick={() => onPageChange(safeTotalPages)}
              disabled={page >= safeTotalPages}
              className={navBtn}
              title={isArabic ? "آخر صفحة" : "Last page"}
            >
              »
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
